import React from 'react';
import { useNavigate } from 'react-router-dom';
import ValidityEditor from './ValidityEditor';
import { formatDate } from '../../utils/formatters';
import { navigeraTillbaka } from '../../../../utils/backNavigation';
import { hamtaBestallarnamn } from '../../../../utils/nameUtils';
import { arExtra, visaBeteckning } from '../../../../utils/offertSpar';
import { hamtaBestallare2 } from '../../../../utils/bestallare2';

/**
 * HeaderSection - Header för offert med status, beställare och projektinfo
 * 
 * Består av:
 * - Rosa header med tillbaka-knapp, versioninfo, datum och action buttons
 * - ValidityEditor för konsulter / validity info för kunder
 * - Beställare 1 & 2 information
 * - Projektinformation
 * 
 * @param {Object} props
 * @param {Object} props.offert - Offert-objektet
 * @param {Object} props.procurement - Procurement-objektet
 * @param {Object} props.user - Inloggad användare
 * @param {boolean} props.isConsultantView - Om det är konsultvy
 * @param {boolean} props.isCustomerView - Om det är kundvy
 * @param {boolean} props.embedded - Om komponenten är inbäddad i modal
 * @param {boolean} props.sending - Om offert håller på att skickas
 * @param {Function} props.onSendOffert - Callback för att skicka offert
 * @param {Function} props.onValidityUpdate - Callback för att uppdatera giltighetstid
 * @param {Function} props.getBackDestination - Funktion som returnerar tillbaka-destination
 */
const HeaderSection = ({
  offert,
  procurement,
  user,
  isConsultantView,
  isCustomerView,
  embedded,
  sending,
  onSendOffert,
  onValidityUpdate,
  getBackDestination,
  onExportPdf,
  exportingPdf,
  onAcceptOnBehalf,
  acceptingOnBehalf
}) => {
  const navigate = useNavigate();

  // Hämta kundinfo från customerId (User) - master för kunddata
  const customer = procurement?.customerId;
  // Rubriken namnger ärendet och ska visa BESTÄLLAREN — föreningen för en BRF,
  // personen annars. Kontaktpersonen visas separat under Beställare 1.
  const displayName = hamtaBestallarnamn(customer) || 'Okänd kund';
  const kontaktperson = customer?.kundtyp === 'brf'
    ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim()
    : '';
  const projectPublicId = procurement?.projectId || '';
  // Registrerad beställare 2 först, annars den som bara är ifylld på leaden
  const bestallare2 = hamtaBestallare2(procurement);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      {/* Rosa header med status, datum och action buttons */}
      <div className="bg-gradient-to-r from-leadme-pink to-leadme-pink-dark text-white p-4 sm:p-6 rounded-t-lg">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          {/* Info-sektion — full bredd på mobil */}
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold">
              {arExtra(offert) ? visaBeteckning(offert) : 'Offert'} - {displayName}
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
              <div className="text-sm">
                {/* Ett tillägg på v1.0 har ingen meningsfull version att visa —
                    beteckningen står i rubriken. Från v1.1 visas den igen. */}
                {arExtra(offert)
                  ? (offert.version && offert.version !== '1.0' && <span>Version {offert.version}</span>)
                  : <span>Version {offert.version || '1.0'}</span>}
              </div>
              <div className="text-sm">
                <span>Skickad {formatDate(offert.sentAt || offert.createdAt)}</span>
              </div>
            </div>

            {/* Validity editor - endast för konsulter */}
            {isConsultantView && (
              <ValidityEditor
                offert={offert}
                onValidityUpdate={onValidityUpdate}
              />
            )}

            {/* Validity info - för kunder */}
            {isCustomerView && offert.expiresAt && (
              <p className="text-sm text-white/90 mt-1">
                Giltig till: {formatDate(offert.expiresAt)}
              </p>
            )}

            <p className="text-xs text-white/80 mt-1">
              Skapad av: {offert.createdBy?.firstName && offert.createdBy?.lastName ?
                `${offert.createdBy.firstName} ${offert.createdBy.lastName}` : 'Okänd byggkonsult'}
            </p>

            {/* Status + kundaktivitet badges */}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {offert.status && offert.status !== 'draft' && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  offert.status === 'accepted' ? 'bg-green-100 text-green-800' :
                  offert.status === 'declined' ? 'bg-red-100 text-red-800' :
                  offert.status === 'viewed' ? 'bg-orange-200 text-orange-900' :
                  offert.status === 'sent' ? 'bg-orange-100 text-orange-800' :
                  offert.status === 'commented' ? 'bg-orange-100 text-orange-800' :
                  offert.status === 'expired' ? 'bg-gray-100 text-gray-800' :
                  offert.status === 'obsolete' ? 'bg-gray-100 text-gray-600' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {offert.status === 'accepted' ? 'Godkänt' :
                   offert.status === 'declined' ? 'Avböjt' :
                   offert.status === 'viewed' ? 'Öppnad' :
                   offert.status === 'sent' ? 'Skickad' :
                   offert.status === 'commented' ? 'Feedback' :
                   offert.status === 'expired' ? 'Utgånget' :
                   offert.status === 'obsolete' ? 'Arkiverad' :
                   offert.status}
                </span>
              )}

              {/* Kundaktivitet — öppningsräknare med färgnivå (endast för konsultvy) */}
              {/* Fallback: gamla offerter har bara viewedAt, räkna som minst 1 */}
              {isConsultantView && (offert.viewCount > 0 || offert.viewedAt) && (() => {
                const views = offert.viewCount > 0 ? offert.viewCount : 1;
                const level = views >= 5 ? 'hög' : views >= 2 ? 'normal' : 'låg';
                const colorClass =
                  level === 'hög' ? 'bg-green-100 text-green-800' :
                  level === 'normal' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-700';
                const label = views === 1 ? '1 gång' : `${views} ggr`;
                const lastViewTs = offert.lastViewedAt || offert.viewedAt;
                const lastView = lastViewTs ? ` • senast ${formatDate(lastViewTs)}` : '';
                return (
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}
                    title={`Aktivitetsnivå: ${level}${lastView}`}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Öppnad {label}
                  </span>
                );
              })()}
            </div>
          </div>

          {/* Action buttons — Tillbaka + övriga knappar */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Tillbaka knapp - endast om inte embedded */}
            {!embedded && (
              <button
                onClick={() => navigeraTillbaka(navigate, getBackDestination())}
                className="px-3 sm:px-4 py-2 text-sm font-medium bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
                <span className="hidden sm:inline">Tillbaka</span>
              </button>
            )}

            {/* Gå till kalkyl - endast för konsulter */}
            {isConsultantView && procurement?._id && (
              <button
                onClick={() => navigate(`/kalkyl/${procurement._id}`, { replace: true })}
                className="px-3 sm:px-4 py-2 text-sm font-medium bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors flex items-center space-x-2"
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <span className="hidden sm:inline">Gå till kalkyl</span>
              </button>
            )}

            {/* Exportera PDF — för konsulter, oavsett stadium.
                Knappen krävde tidigare offert.hasProject, alltså att projektet
                skapats. Men PDF:en klarar ett projekt som inte finns: sektionen
                "Planerad projektstart" är villkorad och faller tillbaka på
                puData och upphandlingen. Och Avtal-mappen finns från bokningen,
                så uppladdningen har någonstans att ta vägen. */}
            {isConsultantView && onExportPdf && (
              <button
                onClick={onExportPdf}
                disabled={exportingPdf}
                className="px-3 sm:px-4 py-2 text-sm font-medium bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportingPdf ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Exporterar...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    <span className="hidden sm:inline">Exportera PDF</span>
                  </>
                )}
              </button>
            )}

            {/* Skicka knapp - endast för konsulter med draft-offert */}
            {isConsultantView && offert.status === 'draft' && (
              <button
                onClick={onSendOffert}
                disabled={sending}
                className="px-3 sm:px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 shadow-sm"
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Skickar offert...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    <span className="hidden sm:inline">Skicka offert</span>
                  </>
                )}
              </button>
            )}

            {/* Godkänn åt kund - BK accepterar offerten åt kunden (t.ex. kund ringde) */}
            {isConsultantView && onAcceptOnBehalf && ['sent', 'viewed', 'commented'].includes(offert.status) && (
              <button
                onClick={onAcceptOnBehalf}
                disabled={acceptingOnBehalf}
                title="Godkänn offerten åt kunden"
                className="px-3 sm:px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 shadow-sm"
              >
                {acceptingOnBehalf ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Godkänner...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    {/* Skrivs ut även på mobil. Det här är enda stället knappen
                        finns, och en grön ikon utan text säger inte vad den gör. */}
                    <span className="whitespace-nowrap">Godkänn åt kund</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Vit sektion med beställare och projektinfo */}
      <div className="p-4 sm:p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {/* Beställare 1 */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Beställare 1</h3>
            <div className="space-y-1">
              <p className="font-semibold text-gray-900">
                {hamtaBestallarnamn(customer) || 'Namn ej angivet'}
              </p>
              {kontaktperson && (
                <p className="text-sm text-gray-600" title="Kontaktperson för föreningen">
                  {kontaktperson}
                </p>
              )}
              <p className="text-sm text-gray-600 break-all">
                {customer?.email || 'E-post ej angiven'}
              </p>
              <p className="text-sm text-gray-600">
                {customer?.phone || 'Telefon ej angivet'}
              </p>
            </div>
          </div>

          {/* Beställare 2 — registrerad eller ännu bara ifylld, se utils/bestallare2 */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Beställare 2</h3>
            <div className="space-y-1">
              {bestallare2 ? (
                <>
                  <p className="font-semibold text-gray-900">
                    {bestallare2.name || 'Namn ej angivet'}
                  </p>
                  <p className="text-sm text-gray-600 break-all">
                    {bestallare2.email || 'E-post ej angiven'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {bestallare2.phone || 'Telefon ej angivet'}
                  </p>
                  {!bestallare2.registrerad && (
                    <p className="text-xs text-gray-500">
                      {bestallare2.email
                        ? 'Konto skapas när offerten skickas'
                        : 'Utan e-postadress skapas inget konto'}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500 italic">Ingen andra beställare</p>
              )}
            </div>
          </div>

          {/* Projekt */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              Projekt {projectPublicId ? `#${projectPublicId}` : ''}
            </h3>
            <div className="space-y-1">
              {/* Adress */}
              <p className="font-semibold text-gray-900">
                {(() => {
                  const projectInfo = procurement?.projectInfo;
                  const addressParts = [];
                  const address = projectInfo?.address || '';
                  const postCode = projectInfo?.postCode || '';
                  const city = projectInfo?.city || '';
                  if (address) addressParts.push(address);
                  if (postCode && city) {
                    addressParts.push(`${postCode} ${city}`);
                  } else if (postCode) {
                    addressParts.push(postCode);
                  } else if (city) {
                    addressParts.push(city);
                  }
                  return addressParts.join(', ') || 'Adress ej angiven';
                })()}
              </p>
              
              {/* Våning & Hiss (om lägenhet) */}
              {(() => {
                const projectInfo = procurement?.projectInfo;
                const isApartment = projectInfo?.propertyType === 'Lägenhet';
                if (isApartment && (projectInfo?.floor || projectInfo?.elevator)) {
                  const floorInfo = [];
                  if (projectInfo.floor) {
                    floorInfo.push(`Våning ${projectInfo.floor}`);
                  }
                  if (projectInfo.elevator) {
                    floorInfo.push(`Hiss: ${projectInfo.elevator}`);
                  }
                  return (
                    <p className="text-sm text-gray-600">
                      {floorInfo.join(' • ')}
                    </p>
                  );
                }
                return null;
              })()}
              
              {/* Projekttyp */}
              <p className="text-sm text-gray-600">
                {procurement?.projectInfo?.projectType || 'Projekttyp ej angiven'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeaderSection;

